import {
  type ConnectionInputSchema,
  SourceDialogMode
} from '../SourceCreate.types';

const SOURCE_TYPE = {
  O365: 2,
  MICROSOFT_SENTINEL: 32,
  MICROSOFT_DEFENDER: 34,
  MICROSOFT: 42,
  MICROSOFT_ENTRA_ID: 50
};

/**
 * Checks if we should show authentication mode selector.
 * Authentication mode is shown for certificate-based services.
 */
export const shouldShowAuthenticationMode = (
  schema: ConnectionInputSchema | null
): boolean => {
  return Boolean(schema?.cert_based_service);
};

/**
 * Checks if we should show certificate mode selector (new vs existing).
 * Shown during creation or regeneration when certificate authentication is selected.
 */
export const shouldShowCertificateModeSelector = (
  mode: SourceDialogMode,
  authMode: string | null,
  isRegenerating: boolean,
  schema: ConnectionInputSchema | null
): boolean => {
  const isCreatingOrRegenerating =
    mode === SourceDialogMode.New ||
    (mode === SourceDialogMode.Edit && isRegenerating);
  return (
    isCreatingOrRegenerating &&
    authMode === 'certificate' &&
    Boolean(schema?.cert_based_service)
  );
};

/**
 * Checks if we should show new certificate fields.
 * Shown when user is creating new source or regenerating certificate in Edit mode.
 */
export const shouldShowNewCertificateFields = (
  mode: string,
  authMode: string | null,
  useNewCertificate: boolean,
  isRegenerating: boolean,
  schema: ConnectionInputSchema | null
): boolean => {
  const isCreatingOrRegenerating =
    mode === SourceDialogMode.New ||
    (mode === SourceDialogMode.Edit && isRegenerating);
  return (
    isCreatingOrRegenerating &&
    authMode === 'certificate' &&
    useNewCertificate &&
    Boolean(schema?.new_certificate_inputs)
  );
};

/**
 * Checks if we should show client secret fields.
 * Shown when authentication mode is set to client secret.
 */
export const shouldShowClientSecretFields = (
  authMode: string | null,
  schema: ConnectionInputSchema | null
): boolean => {
  return authMode === 'client_secret' && Boolean(schema?.client_secret_inputs);
};

/**
 * Checks if we should show existing certificate upload fields.
 * Shown when user selects to upload an existing certificate.
 */
export const shouldShowExistingCertificateFields = (
  mode: string,
  authMode: string | null,
  useNewCertificate: boolean,
  isRegenerating: boolean,
  schema: ConnectionInputSchema | null
): boolean => {
  const isCreatingOrRegenerating =
    mode === SourceDialogMode.New ||
    (mode === SourceDialogMode.Edit && isRegenerating);
  return (
    isCreatingOrRegenerating &&
    authMode === 'certificate' &&
    !useNewCertificate &&
    Boolean(schema?.existing_certificate_inputs)
  );
};

/**
 * Checks if we should show download/regenerate certificate controls.
 * Only shown in Edit mode for Microsoft sources with certificate authentication.
 */
export const shouldShowCertificateControls = (
  mode: string,
  sourceTypeId: number,
  authMode: string | null,
  isRegenerating: boolean
): boolean => {
  const microsoftSourceTypes = [
    SOURCE_TYPE.O365,
    SOURCE_TYPE.MICROSOFT_SENTINEL,
    SOURCE_TYPE.MICROSOFT_DEFENDER,
    SOURCE_TYPE.MICROSOFT,
    SOURCE_TYPE.MICROSOFT_ENTRA_ID
  ];

  return (
    mode === SourceDialogMode.Edit &&
    microsoftSourceTypes.includes(sourceTypeId) &&
    !isRegenerating &&
    authMode === 'certificate'
  );
};
